import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, finalize, take } from 'rxjs/operators';
import * as olci from '../../../actions/olci';
import { GuestService } from '../../../modules/core/services/guest.service';
import { BookingService } from '../../../modules/core/services/booking.service';
import { GatingServices } from '../../../modules/core/services/gating.services';
import { MicroServicesService } from '../../../modules/core/services/micro-services.service';
import * as fromRoot from '../../../reducers';

export interface EndSessionOptions {
	exitUrl?: string;
	logoutFromGA?: boolean;
}

@Injectable({
	providedIn: 'root',
})
export class AuthenticationService {
	private readonly LOGOUT_DELAY_MS = 500;

	private readonly store = inject(Store<fromRoot.AppState>);
	private readonly gatingServices = inject(GatingServices);
	private readonly bookingService = inject(BookingService);
	private readonly microServices = inject(MicroServicesService);
	private readonly guestService = inject(GuestService);

	public endSession(options: EndSessionOptions = {}): void {
		const redirectUrl = options.exitUrl ?? this.gatingServices.getGAAccountURL();
		this.sessionEndMicroservice().pipe(take(1)).subscribe(() => {
			if (options.logoutFromGA) {
				this.handleGuestAccountLogout(redirectUrl);
			} else {
				this.clearOlciAndRedirect(redirectUrl);
			}
		});
	}

	private clearOlciAndRedirect(url: string): void {
		this.store.dispatch(new olci.ClearStateOnlyAction()); 
		window.location.href = url;
	}

	private handleGuestAccountLogout(redirectUrl: string): void {
		setTimeout(() => {
			this.bookingService.guestAccountsLogout()?.pipe(
				finalize(() => this.redirectAndClearState(redirectUrl))
			).subscribe();
		}, this.LOGOUT_DELAY_MS);
	}

	private redirectAndClearState(url: string): void {
		this.bookingService.guestAccountsClearCookies();
		this.bookingService.deleteSessionData();
		this.clearOlciAndRedirect(url);
	}

	private sessionEndMicroservice(): Observable<void> {
		const checkinToken = this.bookingService.checkinToken;
		const selectedGuests = this.guestService.getAllGuestsBookingCombination(
			this.guestService.selectedForCheckin
		);
		if (!checkinToken || !selectedGuests?.length) {
			return of(undefined);
		}
		const payload = {
			checkinToken,
			guests: selectedGuests,
		};
		return this.microServices.guestCheckinEnd(payload).pipe(
			catchError(() => of(undefined))
		);
	}
}
